import { FormEventHandler, useMemo } from 'react'
import { useState } from 'react'
import { Button } from '@bm-js/atlascloud-ui'
import { v4 as uuid } from 'uuid'
import Input from './Input'
import Checkbox from './Checkbox'
import {
  ChangeEvent,
  encodeSweUrlComponent,
  FeatureKey,
  grid,
  WebsiteConfigPage,
} from '@bm-js/atlascloud-shared'
import { reservedPageNames } from '../cnst/editor.cnst'
import { useTr } from '../utils/useTr'
import { TrKey } from '../cnst/translations/trKey.cnst'
import { FormProps } from '../contexts/Form.context'

const PageForm = ({
  initialValue,
  onSubmit,
  onAbort,
  extras,
}: FormProps<WebsiteConfigPage, { features: string[] }>) => {
  const { features } = extras
  const { tr } = useTr()
  const fallbackData: Partial<WebsiteConfigPage> = {
    viewName: '',
    authOnly: false,
  }

  const [formData, setFormData] = useState(initialValue || fallbackData)
  const [error, setError] = useState<string | null>(null)

  const submit: FormEventHandler = (e) => {
    e.preventDefault()
    let errorMessage: string | null = null
    if (!formData.viewName) {
      errorMessage = tr(TrKey.editorPageFormPageNameMissing)
    }
    const name = encodeSweUrlComponent(formData.viewName || '')

    const isReserved = reservedPageNames.includes(name)
    if (isReserved) {
      errorMessage = tr(TrKey.editorPageFormPageNameReserved)
    }

    if (errorMessage) {
      setError(errorMessage)
      return
    }

    const updatedFormData: WebsiteConfigPage = {
      ...formData,
      name,
      uuid: formData.uuid || uuid(),
      viewName: formData.viewName || '',
      authOnly: formData.authOnly || false,
      content: formData.content || [],
      children: formData.children || [],
    }

    // If there is no section on the page, a user cannot add anything
    if (!updatedFormData.content.length) {
      const newSection = { ...grid, uuid: uuid() }
      updatedFormData.content.push(newSection)
    }

    onSubmit(updatedFormData)
  }

  const handleChange = (e: ChangeEvent) => {
    setError(null)
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  const authEnabled = useMemo(() => {
    return features.includes(FeatureKey.customerUserLogins)
  }, [features])

  return (
    <form onSubmit={submit}>
      <Input
        label={tr(TrKey.editorPageFormPageNameLabel)}
        desc={tr(TrKey.editorPageFormPageNameDescription)}
        type='text'
        required
        value={formData.viewName}
        name='viewName'
        id='viewName'
        onChange={handleChange}
      />
      {error && <p className='-mt-6 mb-4 text-sm text-red-500'>{error}</p>}
      {authEnabled && (
        <Checkbox
          name='authOnly'
          checked={!!formData.authOnly}
          label={tr(TrKey.editorPageFormAuthOnlyLabel)}
          desc={tr(TrKey.editorPageFormAuthOnlyDescription)}
          onChange={(e) =>
            setFormData({ ...formData, authOnly: e.target.checked })
          }
        />
      )}
      <div className='jce ac gap05 flex'>
        <Button type='submit'>{tr(TrKey.save)}</Button>
        <Button danger onClick={onAbort}>
          {tr(TrKey.cancel)}
        </Button>
      </div>
    </form>
  )
}

export default PageForm
